import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';

import { FullPageSpinner } from 'admin-components/components';
import { FormattedMessage } from 'react-intl';
import FormattedCurrency from 'components/ui/FormattedCurrency';
import { Column, Row } from 'components/shared/Layout';
import Tabs from 'components/ui/Tabs';
import { productFamilyCodes } from 'types/productFamilyCodes';

import { paymentMethods, statusShortCode, codes, methods } from 'constants/payment';
import ReviewBookingSummary from 'components/ui/ReviewBookingSummary';
import { LinkButton } from 'components/ui/Button';

import { historyShape, userShape, paymentsShape, customerShape, legShape, passengersShape, productsShape } from 'constants/propTypes';
import { getAvailablePaymentTypes } from 'utils/payment';

import PaymentMethod from 'pages/BookingDetails/Payments/PaymentMethod';
import { isBookingEHL, paymentsTabsTypesForEHL, splitPaymentsList, splitPaymentsSum, getIsPackageBookingOnly } from './Payments.utils';

import AlertProvider from './AlertProvider';
import SplitPaymentMethod from './SplitPaymentMethod';
import { paymentTypeValues } from './Payments.constants';
import PutOnHoldNoDepositButton from './PutOnHoldNoDepositButton';
import Currency from 'utils/Currency';

import {
  PaymentSummary,
  PaymentContainer,
  PaymentSelectionContainer,
  StyledRadioFieldSet,
  StyledPaymentContainer,
  StyledAmount,
  StyledPayments,
  StyledSpacedApart,
  StyledAmountDue,
  Divider
} from './Payments.styles';
import { bookingStatus } from 'types/booking';

const Payments = ({
  history: { location, push },
  availableMethods,
  payAndConfirm,
  takeDeposit,
  addPayment,
  createPaymentLink,
  loading,
  user,
  paymentUrlPrefix,
  reference,
  customer,
  currency,
  status,
  totalPriceToBePaid,
  payments,
  amountDue,
  isPackage,
  isLoyalty,
  hasHotel,
  depositAmount,
  cancelPayments,
  outbound,
  inbound,
  passengers,
  productFamilies = [],
  isExchange = false,
  additionalDetails = [],
  isAssociatedBookingEHL = false,
  pendingCancellationLegs = [],
  isExtrasOnly = false,
  isInAfterSales = false,
  isAriaBooking,
  isGroup = false,
  totalPrice,
  changeExpiryTimestamp
}) => {
  const havePendingSocialMediaPayment = payments.some(
    (payment) => payment.paymentStatus === statusShortCode.pending && payment.method === codes.SOCIAL_MEDIA
  );
  const hash = location.hash
    ? location.hash.substring(1)
    : havePendingSocialMediaPayment
    ? methods.SOCIAL_MEDIA
    : availableMethods?.[0].code;
  const isUpgradeWithPointsPath = new URLSearchParams(location?.search).get('isUpgradeWithPoints');
  const isImpulseUpgradePath = new URLSearchParams(location?.search).get('isImpulseUpgrade');
  const isFlowForwardPath = new URLSearchParams(location?.search).get('isFlowForward');
  const isMissedDeparturePath = new URLSearchParams(location?.search).get('isMissedDeparture');
  const membershipNumber = new URLSearchParams(location?.search).get('membershipNumber');
  const isSubscription = new URLSearchParams(location?.search).get('isSubscription') === 'true';
  const addPassengers = new URLSearchParams(location?.search).get('addPassengers');
  const isExchangeFlow = location?.pathname.includes('exchange');
  const putOnHoldNoDepositMessageId = isSubscription
    ? 'subscriptions.putOnHoldNoDeposit'
    : depositAmount > 0
    ? 'payments.putTemporarilyOnHoldNoDeposit'
    : 'payments.putOnHoldNoDeposit';

  const isTempOnHoldAddPassenger = status === bookingStatus.TEMPORARILY_ON_HOLD && changeExpiryTimestamp && addPassengers;
  const paymentTypes = useMemo(
    () => ({
      PAY_BALANCE: {
        label: (
          <label htmlFor="payment-type-pay-in-full" data-cy="pay-in-full">
            <FormattedMessage id="payments.payInFull" />
          </label>
        ),
        value: paymentTypeValues.PAY_BALANCE
      },
      ON_HOLD_WITH_DEPOSIT: {
        label: (
          <label htmlFor="payment-type-put-on-hold-with-deposit" data-cy="put-on-hold-with-deposit">
            <FormattedMessage id="payments.putOnHoldWithDeposit" />
          </label>
        ),
        value: paymentTypeValues.ON_HOLD_WITH_DEPOSIT
      },
      ON_HOLD_NO_DEPOSIT: {
        label: (
          <label htmlFor="payment-type-put-on-hold-no-deposit" data-cy="put-on-hold-no-deposit">
            <FormattedMessage id={putOnHoldNoDepositMessageId} />
          </label>
        ),
        value: paymentTypeValues.ON_HOLD_NO_DEPOSIT
      }
    }),
    [putOnHoldNoDepositMessageId]
  );

  const hideLoyaltyPaymentForSpecificExchangedFlow = !!(isImpulseUpgradePath || isFlowForwardPath || isMissedDeparturePath);

  const allTicketsBeingExchangedArePublic = pendingCancellationLegs?.every((pdl) =>
    pdl?.products?.every((p) => p?.productFamily?.familyGroup === productFamilyCodes.PUB)
  );

  const cancelVoucherRoute = location.pathname.includes('payment') && location.search.includes('cancel-voucher');

  const [paymentType, changePaymentType] = useState(
    location?.state?.paymentTypesSet ? location?.state?.paymentTypesSet : paymentTypeValues.PAY_BALANCE
  );

  const isPutOnHoldWithDeposit = isTempOnHoldAddPassenger ? true : paymentType === paymentTypeValues.ON_HOLD_WITH_DEPOSIT;
  const listOfSplitPayments = splitPaymentsList(payments);
  const sumOfSplitPayments = splitPaymentsSum(listOfSplitPayments);

  let outstandingDepositAmountToPay = Math.max(0, +(depositAmount - sumOfSplitPayments).toFixed(2));

  if (isTempOnHoldAddPassenger && isExchangeFlow) {
    const difference = Currency.subtract(totalPrice, totalPriceToBePaid);
    outstandingDepositAmountToPay = Currency.subtract(outstandingDepositAmountToPay, difference);
  }

  const paymentAmountToUse = isPutOnHoldWithDeposit ? String(outstandingDepositAmountToPay) : String(totalPriceToBePaid);

  const [paymentAmount, changePaymentAmount] = useState(paymentAmountToUse);
  const [paymentMethod, changePaymentMethod] = useState(hash);

  const isBalance = paymentType === paymentTypeValues.PAY_BALANCE;
  const isOnHoldNoDeposit = paymentType === paymentTypeValues.ON_HOLD_NO_DEPOSIT;
  const shouldHideLoyaltyPaymentMethod = isExchange && !allTicketsBeingExchangedArePublic;
  const maximumAmountWithoutDepositOnHold = isBalance ? totalPriceToBePaid : depositAmount;
  const maximumAmount = isPutOnHoldWithDeposit ? outstandingDepositAmountToPay : maximumAmountWithoutDepositOnHold;

  const shouldHideLoyaltyPaymentMethodForSpecificFlows = isExchange && hideLoyaltyPaymentForSpecificExchangedFlow;

  const availablePaymentTypes = getAvailablePaymentTypes({
    totalPriceToBePaid,
    depositAmount,
    status,
    outbound,
    inbound
  });

  useEffect(() => {
    // eslint-disable-next-line no-unused-expressions
    !isTempOnHoldAddPassenger
      ? paymentType === paymentTypeValues.ON_HOLD_WITH_DEPOSIT
        ? changePaymentAmount(String(outstandingDepositAmountToPay))
        : changePaymentAmount(String(totalPriceToBePaid))
      : changePaymentAmount(String(outstandingDepositAmountToPay));
  }, [totalPriceToBePaid, changePaymentAmount, outstandingDepositAmountToPay, paymentType, isTempOnHoldAddPassenger]);

  if (loading) {
    return <FullPageSpinner />;
  }

  const handlePaymentTypeChange = (e) => {
    const newPaymentType = e.target.value;

    changePaymentType(newPaymentType);

    if (newPaymentType === paymentTypeValues.PAY_BALANCE) {
      changePaymentAmount(String(totalPriceToBePaid));
    }

    if (newPaymentType === paymentTypeValues.ON_HOLD_WITH_DEPOSIT) {
      changePaymentAmount(String(outstandingDepositAmountToPay));
    }

    push(`${location.pathname}${location.search}#${hash}`, {
      paymentTypesSet: newPaymentType
    });
  };

  const onClick = async () => {
    await cancelPayments();
  };

  const paymentsToDisplay = payments.filter(
    (payment) =>
      payment.createdRevision === null &&
      (payment.isSuccessful ||
        (payment.paymentStatus === statusShortCode.pending && payment.method === codes.VOUCHER && payment.amount !== 0))
  );

  const paymentsTabsToDisplay = availableMethods
    .filter(({ code }) => (paymentType === paymentTypeValues.ON_HOLD_NO_DEPOSIT || isLoyalty ? code !== methods.SOCIAL_MEDIA : true))
    .filter(({ code }) => !isLoyalty || code !== methods.LOYALTY_POINTS)
    .filter(({ code }) => (isUpgradeWithPointsPath === 'true' ? code !== methods.LOYALTY_POINTS : true))
    .filter(({ code }) =>
      shouldHideLoyaltyPaymentMethod || shouldHideLoyaltyPaymentMethodForSpecificFlows ? code !== methods.LOYALTY_POINTS : true
    )
    .filter(({ code }) => (status === bookingStatus.UNDERPAID ? code !== methods.SOCIAL_MEDIA : true))
    .map(({ code }) => paymentMethods[code]);

  const isPackageBookingOnly = getIsPackageBookingOnly(additionalDetails);
  const paymentsTabs =
    isBookingEHL(additionalDetails) || hasHotel || isAssociatedBookingEHL
      ? paymentsTabsTypesForEHL(paymentsTabsToDisplay, isPackageBookingOnly)
      : paymentsTabsToDisplay;

  const isStationUser = user?.paymentLocation?.isStation;
  const userDefaultCurrency = user?.paymentCurrencies?.[0] || 'GBP';

  const applyPaymentsFilterForStations = paymentsTabs.map((tab) => ({
    ...tab,
    disabled: Boolean(
      (isStationUser && currency !== userDefaultCurrency && tab.value === methods.CASH) ||
        (havePendingSocialMediaPayment && tab.value !== methods.SOCIAL_MEDIA)
    ),
    disabledOnHoverMessage:
      havePendingSocialMediaPayment && tab.value !== methods.SOCIAL_MEDIA ? 'paymentLink.inProgress' : 'stations.cashLocalCurrencyOnly'
  }));

  return (
    <ReviewBookingSummary title="payments.makePayment">
      <AlertProvider>
        <Row>
          {!isTempOnHoldAddPassenger && (
            <StyledRadioFieldSet
              name="payment-type"
              onChange={handlePaymentTypeChange}
              selectedValue={paymentType}
              radioButtons={availablePaymentTypes.map(({ type }) => paymentTypes[type])}
            />
          )}
          {!isOnHoldNoDeposit && (
            <PaymentSelectionContainer alignItems="center">
              <h2>
                <FormattedMessage id="payments.paymentTypes" />
              </h2>

              {!cancelVoucherRoute && (
                <Tabs
                  overflowScroll={true}
                  onClick={changePaymentMethod}
                  selectedValue={paymentMethod || availableMethods?.[0].code}
                  tabs={applyPaymentsFilterForStations}
                />
              )}
            </PaymentSelectionContainer>
          )}
          <PaymentContainer columns={12}>
            <PaymentSummary span={5}>
              <StyledAmount data-cy="amount-due">
                <h3>
                  {isPutOnHoldWithDeposit ? (
                    <FormattedMessage id="payments.amountDueWithDeposit" />
                  ) : (
                    <FormattedMessage id="payments.amountDue" />
                  )}
                </h3>
                <StyledAmountDue>
                  {isPutOnHoldWithDeposit ? (
                    <FormattedCurrency
                      amount={isTempOnHoldAddPassenger ? outstandingDepositAmountToPay : depositAmount}
                      currency={amountDue.currency}
                    />
                  ) : (
                    <FormattedCurrency amount={amountDue.amount} currency={amountDue.currency} />
                  )}
                </StyledAmountDue>
                <h4>
                  <FormattedMessage id="payments.title" />
                </h4>
              </StyledAmount>
              <StyledPayments data-cy="provisional-payments">
                {paymentsToDisplay.map((payment) => (
                  <li key={payment.refId}>
                    <StyledSpacedApart>
                      <PaymentMethod method={payment.method} />
                      <Divider />
                      <FormattedCurrency amount={payment.amount} currency={payment.currency} />
                    </StyledSpacedApart>
                  </li>
                ))}
              </StyledPayments>
              {paymentsToDisplay.length > 0 && (
                <div>
                  <LinkButton data-cy="cancel-payments" loading={loading} onClick={onClick} padding={'0px'}>
                    <FormattedMessage id="payments.cancelPayments" />
                  </LinkButton>
                </div>
              )}
              <StyledSpacedApart>
                <FormattedMessage id="payments.totalOutstanding" />
                <Divider />
                {isPutOnHoldWithDeposit ? (
                  <FormattedCurrency amount={outstandingDepositAmountToPay} currency={currency} />
                ) : (
                  <FormattedCurrency amount={totalPriceToBePaid} currency={currency} />
                )}
              </StyledSpacedApart>
            </PaymentSummary>

            <StyledPaymentContainer span={7}>
              {!isOnHoldNoDeposit && !isAriaBooking ? (
                <SplitPaymentMethod
                  reference={reference}
                  currency={currency}
                  customer={customer}
                  isPackage={isPackage}
                  isLoyalty={isLoyalty}
                  hasHotel={hasHotel}
                  addPayment={addPayment}
                  createPaymentLink={createPaymentLink}
                  currentAmount={paymentAmount}
                  isPutOnHoldWithDeposit={isTempOnHoldAddPassenger ? false : isPutOnHoldWithDeposit}
                  isOnHold={paymentType !== paymentTypeValues.PAY_BALANCE || isTempOnHoldAddPassenger}
                  loading={loading}
                  maximumAmount={maximumAmount}
                  methodCode={paymentMethod.substring(paymentMethod.indexOf('_') + 1)}
                  onAmountChange={changePaymentAmount}
                  payAndConfirm={payAndConfirm}
                  takeDeposit={takeDeposit}
                  user={user}
                  isCancelVoucherRoute={cancelVoucherRoute}
                  payments={payments}
                  outbound={outbound}
                  inbound={inbound}
                  passengers={passengers}
                  productFamilies={productFamilies}
                  additionalDetails={additionalDetails}
                  isExchange={isExchange}
                  isExtrasOnly={isExtrasOnly}
                  isInAfterSales={isInAfterSales}
                  status={status}
                  isGroup={isGroup}
                />
              ) : isSubscription ? (
                <>
                  <div style={{ marginBottom: '16px' }}>
                    <FormattedMessage id="subscriptions.tooltip.description" />
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <b>
                      <FormattedMessage id="subscriptions.bankTransferCompany" />
                    </b>
                  </div>
                  <Column span={12}>
                    <Row style={{ margin: 0 }}>
                      <Column span={3}>
                        <b>
                          <FormattedMessage id="subscriptions.tooltip.iban" />:
                        </b>
                      </Column>
                      <Column span={9}>GB56RBOS16106503200158</Column>
                    </Row>
                    <Row style={{ margin: 0 }}>
                      <Column span={3}>
                        <b>
                          <FormattedMessage id="subscriptions.tooltip.bic" />:
                        </b>
                      </Column>
                      <Column span={9}>RBOSGB2L</Column>
                    </Row>
                    <Row style={{ margin: 0 }}>
                      <Column span={3}>
                        <b>
                          <FormattedMessage id="subscriptions.tooltip.amount" />:
                        </b>
                      </Column>
                      <Column span={9}>
                        <FormattedCurrency amount={paymentAmount} currency={currency} />
                      </Column>
                    </Row>
                    <Row style={{ margin: 0 }}>
                      <Column span={3}>
                        <b>
                          <FormattedMessage id="paymentLink.reference" />:
                        </b>
                      </Column>
                      <Column span={9}>{reference}</Column>
                    </Row>
                  </Column>
                  <PutOnHoldNoDepositButton
                    depositAmount={depositAmount}
                    bookingReference={reference}
                    paymentUrlPrefix={paymentUrlPrefix}
                    isSubscription={isSubscription}
                    membershipNumber={membershipNumber}
                    isDisabled={havePendingSocialMediaPayment}
                  >
                    <FormattedMessage id={putOnHoldNoDepositMessageId} />
                  </PutOnHoldNoDepositButton>
                </>
              ) : isAriaBooking ? (
                <>
                  <b>
                    <FormattedMessage id="confirm.onlyOnAria" />
                  </b>
                </>
              ) : (
                <PutOnHoldNoDepositButton
                  depositAmount={depositAmount}
                  bookingReference={reference}
                  paymentUrlPrefix={paymentUrlPrefix}
                  isSubscription={isSubscription}
                  membershipNumber={membershipNumber}
                  isDisabled={havePendingSocialMediaPayment}
                >
                  <FormattedMessage id={putOnHoldNoDepositMessageId} />
                </PutOnHoldNoDepositButton>
              )}
            </StyledPaymentContainer>
          </PaymentContainer>
        </Row>
      </AlertProvider>
    </ReviewBookingSummary>
  );
};

Payments.propTypes = {
  user: userShape,
  history: historyShape,
  loading: PropTypes.bool,
  availableMethods: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string
    })
  ),
  payAndConfirm: PropTypes.func,
  takeDeposit: PropTypes.func,
  addPayment: PropTypes.func,
  createPaymentLink: PropTypes.func,
  paymentUrlPrefix: PropTypes.string,
  customer: customerShape,
  reference: PropTypes.string,
  currency: PropTypes.string,
  status: PropTypes.string,
  totalPriceToBePaid: PropTypes.number,
  payments: paymentsShape,
  amountDue: PropTypes.shape({
    amount: PropTypes.number,
    currency: PropTypes.string
  }),
  isPackage: PropTypes.bool,
  isLoyalty: PropTypes.bool,
  hasHotel: PropTypes.bool,
  depositAmount: PropTypes.number,
  cancelPayments: PropTypes.func.isRequired,
  outbound: PropTypes.arrayOf(legShape),
  inbound: PropTypes.arrayOf(legShape),
  passengers: passengersShape,
  productFamilies: productsShape,
  isExchange: PropTypes.bool,
  additionalDetails: PropTypes.arrayOf(
    PropTypes.shape({
      itemRef: PropTypes.string,
      key: PropTypes.string,
      value: PropTypes.string
    })
  ),
  isAssociatedBookingEHL: PropTypes.bool,
  pendingCancellationLegs: PropTypes.arrayOf(legShape),
  isExtrasOnly: PropTypes.bool,
  isInAfterSales: PropTypes.bool,
  isAriaBooking: PropTypes.bool,
  isGroup: PropTypes.bool,
  totalPrice: PropTypes.number,
  changeExpiryTimestamp: PropTypes.string
};

export default Payments;
